import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, GraduationCap, Search, Calendar, CalendarDays, BookOpen, Users, MapPin, Settings, BarChart3, ChevronRight } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { academyAPI } from '../../services/api';
import BottomSheet from '../../components/common/BottomSheet';

const MAX_ACADEMIES = 3;
const MAX_PROGRAMS  = 3;

/**
 * Mobile bottom-sheet quick-nav for Academies, matching the Squads and
 * Tournaments panels.
 *
 * Lists the academies the user actually runs — created *or* staffed, which is
 * why the list request is `tab=my-academies`: that tab now matches both, so a
 * head coach reaches their academy here the same as its owner does.
 *
 * The running programs come back on the list response itself rather than being
 * fetched per academy, so opening this panel is one request no matter how many
 * academies are shown.
 */
const AcademiesQuickPanel = ({ isOpen, onClose }) => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [academies, setAcademies] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await academyAPI.getAll({ tab: 'my-academies', page: 1, limit: MAX_ACADEMIES });
      setAcademies(Array.isArray(res?.academies) ? res.academies : []);
    } catch {
      setAcademies([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) load();
  }, [isOpen, load]);

  // The portal route reads better and works for both owners and staff.
  const go = (path) => { onClose(); navigate(path); };
  const academyPath = (a, query = '') => `/academy/${a.slug || a.id}${query}`;

  // A door that opens on nothing is worse than no door. Being staff is not the
  // same as being able to DO anything: a coach with no grants reaches Settings
  // and finds an empty screen, and Reports 403s. So these follow the same rules
  // the settings screen itself uses — owner/admin, or the specific capability.
  const canOpenSettings = (a) => a.isAdmin || (a.myCapabilities || []).length > 0;
  const canSeeReports   = (a) => a.isAdmin || (a.myCapabilities || []).includes('view_reports');

  const QuickLink = ({ icon: Icon, label, onClick }) => (
    <button
      onClick={onClick}
      className={`flex-1 flex items-center justify-center gap-1 py-2 rounded-lg border text-xs font-medium ${theme.cardHover} ${theme.border} ${theme.textSecondary}`}
    >
      <Icon className="w-3.5 h-3.5" /> {label}
    </button>
  );

  return (
    <BottomSheet isOpen={isOpen} onClose={onClose}>
      <div className="flex items-center justify-between px-4 pt-3 pb-2">
        <div className="flex items-center gap-2">
          <GraduationCap className={`w-5 h-5 ${theme.primary}`} />
          <span className={`font-semibold ${theme.text}`}>Your Academies</span>
        </div>
        <button onClick={onClose} className={`p-1.5 rounded-lg ${theme.cardHover}`}>
          <X className={`w-4 h-4 ${theme.textMuted}`} />
        </button>
      </div>

      <div className={`h-px ${theme.border} bg-current opacity-10 mx-4`} />

      <div className="px-4 py-3">
        {loading ? (
          <div className="flex justify-center py-8">
            <div className={`w-6 h-6 border-2 border-t-transparent rounded-full animate-spin ${theme.primary.replace('text-', 'border-')}`} />
          </div>
        ) : academies.length === 0 ? (
          <div className="text-center py-8">
            <GraduationCap className="w-10 h-10 text-gray-300 mx-auto mb-2" />
            <p className={`text-sm ${theme.textMuted} mb-3`}>You don't run an academy yet</p>
          </div>
        ) : (
          <div className="space-y-3 mb-3">
            {academies.map(a => {
              const programs = (a.activePrograms || []).slice(0, MAX_PROGRAMS);
              return (
                <div key={a.id} className={`rounded-xl border ${theme.border} ${theme.backgroundSecondary} p-3`}>
                  <button
                    onClick={() => go(academyPath(a))}
                    className="flex items-center justify-between w-full mb-2 text-left gap-2"
                  >
                    <span className={`font-semibold text-sm truncate ${theme.text}`}>{a.name}</span>
                    <span className={`text-xs flex-shrink-0 capitalize ${theme.textMuted}`}>{a.userRole || ''}</span>
                  </button>

                  {programs.length > 0 && (
                    <div className="mb-2 space-y-1">
                      {programs.map(p => (
                        <button
                          key={p.id}
                          onClick={() => go(academyPath(a, `?tab=programs&program=${p.id}`))}
                          className={`flex items-center justify-between w-full px-2 py-1.5 rounded-lg text-left ${theme.cardHover}`}
                        >
                          <span className={`flex items-center gap-1.5 min-w-0 text-xs ${theme.textSecondary}`}>
                            <Calendar className="w-3.5 h-3.5 flex-shrink-0" />
                            <span className="truncate">{p.name}</span>
                          </span>
                          <ChevronRight className={`w-3.5 h-3.5 flex-shrink-0 ${theme.textMuted}`} />
                        </button>
                      ))}
                    </div>
                  )}

                  {/* The things people actually come here for, first. Two rows
                      rather than one: six targets on a 375px phone leaves each
                      under 60px, which is below a comfortable tap. */}
                  <div className="flex gap-2">
                    <QuickLink icon={CalendarDays} label="Open Play" onClick={() => go(academyPath(a, '?tab=play'))} />
                    <QuickLink icon={BookOpen}     label="Programs"  onClick={() => go(academyPath(a, '?tab=programs'))} />
                    <QuickLink icon={Users}        label="Staff"     onClick={() => go(academyPath(a, '?tab=staff'))} />
                  </div>

                  <div className="flex gap-2 mt-2">
                    <QuickLink icon={MapPin} label="Courts" onClick={() => go(academyPath(a, '?tab=courts'))} />
                    {canOpenSettings(a) && (
                      <QuickLink icon={Settings}  label="Settings" onClick={() => go(academyPath(a, '?tab=settings'))} />
                    )}
                    {canSeeReports(a) && (
                      <QuickLink icon={BarChart3} label="Reports"  onClick={() => go(academyPath(a, '?tab=settings&settings=manage-reports'))} />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <button
          onClick={() => go('/academies')}
          className={`flex items-center justify-between w-full py-2.5 px-3 rounded-lg ${theme.cardHover} ${theme.textSecondary} text-sm font-medium`}
        >
          <span className="flex items-center gap-2"><Search className="w-4 h-4" /> Browse all academies</span>
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </BottomSheet>
  );
};

export default AcademiesQuickPanel;
